import { Injectable, signal, WritableSignal, computed, Signal, effect, OnDestroy } from '@angular/core';
import { CoordinatesSystem, DeviceSpecificBreakpoints, MaxAspectRatioBreakpoints, NoBreakpoints, ScreenWidthBreakpoints } from '../model/enums/screen-size.enums';
import { BehaviorSubject, Subscription } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LoggerService } from '../../services/logger.service';

@Injectable({
  providedIn: 'root'
})
export class ScreensizeStateService implements OnDestroy {
  private subscription: Subscription = new Subscription();

  private maxAspectRatioSubject = new BehaviorSubject<MaxAspectRatioBreakpoints>(MaxAspectRatioBreakpoints.Landscape);
  private maxAspectRatio$: Signal<MaxAspectRatioBreakpoints> = toSignal(this.maxAspectRatioSubject.asObservable(), { initialValue: MaxAspectRatioBreakpoints.Landscape });
  private screenWidthSubject = new BehaviorSubject<ScreenWidthBreakpoints>(ScreenWidthBreakpoints.Large);
  private screenWidth$: Signal<ScreenWidthBreakpoints> = toSignal(this.screenWidthSubject.asObservable(), { initialValue: ScreenWidthBreakpoints.Large });

  private _screenSizes: WritableSignal<Record<CoordinatesSystem, MaxAspectRatioBreakpoints | DeviceSpecificBreakpoints | ScreenWidthBreakpoints | NoBreakpoints>> = signal({
    [CoordinatesSystem.DeviceSpecific]: DeviceSpecificBreakpoints.Web,
    [CoordinatesSystem.MaxAspectRatio]: MaxAspectRatioBreakpoints.Landscape,
    [CoordinatesSystem.ScreenWidth]: ScreenWidthBreakpoints.Large,
    [CoordinatesSystem.NoCoordinates]: NoBreakpoints.Unset,

  });
  private _screenSizeChanges: WritableSignal<number> = signal(0);

  constructor(
    private logger: LoggerService,
    private breakpointObserver: BreakpointObserver) {
    this.initializeMaxAspectRatioObserver();

    effect(() => {
      const maxAspectRatio = this.maxAspectRatio$();
      const screenWidth = this.screenWidth$();
      this._screenSizes.update(state => ({
        ...state,
        [CoordinatesSystem.MaxAspectRatio]: maxAspectRatio,
        [CoordinatesSystem.ScreenWidth]: screenWidth
      }));
      this._screenSizeChanges.update(state => state + 1);
    });

    effect(() => {
      this.logger.log('ScreenSizes:', this._screenSizes());
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) this.subscription.unsubscribe();
  }

  get screenSizes(): Signal<Record<CoordinatesSystem, MaxAspectRatioBreakpoints | DeviceSpecificBreakpoints | ScreenWidthBreakpoints | NoBreakpoints>> {
    return this._screenSizes.asReadonly();
  }

  get screenSizeChanges(): Signal<number> {
    return this._screenSizeChanges.asReadonly();
  }

  public isScreenSize(coordinateSystem: CoordinatesSystem, breakpoint: MaxAspectRatioBreakpoints | DeviceSpecificBreakpoints | ScreenWidthBreakpoints | NoBreakpoints): Signal<boolean> {
    return computed(() => this._screenSizes()[coordinateSystem] === breakpoint.valueOf());
  }

  public isScreenSizes(coordinateSystem: CoordinatesSystem, breakpoints: (MaxAspectRatioBreakpoints | DeviceSpecificBreakpoints | ScreenWidthBreakpoints | NoBreakpoints)[]): Signal<boolean> {
    return computed(() => breakpoints.includes(this._screenSizes()[coordinateSystem]));
  }

  // ### PRIVATE METHODS ###
  private initializeMaxAspectRatioObserver(): void {
    this.subscription =
      this.breakpointObserver.observe([
        '(max-aspect-ratio: 9/16)',
        '(max-aspect-ratio: 3/4)',
        '(max-aspect-ratio: 1/1)',
        '(max-aspect-ratio: 4/3)',
        '(max-aspect-ratio: 16/9)',
        '(min-width: 1920px)', // XLarge	(min-width: 1920px)
        '(min-width: 1280px)', // Large	(min-width: 1280px) and (max-width: 1919.98px)     
        '(min-width: 960px)', // Medium	(min-width: 960px) and (max-width: 1279.98px)
        '(min-width: 600px)', // Small	(min-width: 600px) and (max-width: 959.98px)
      ]).subscribe(result => {
        this.logger.log('BreakpointObserver - ScreenSizes:', result.breakpoints);
        if (result.matches) {
          if (result.breakpoints['(max-aspect-ratio: 9/16)']) {
            this.maxAspectRatioSubject.next(MaxAspectRatioBreakpoints.Portrait);
          } else if (result.breakpoints['(max-aspect-ratio: 3/4)']) {
            this.maxAspectRatioSubject.next(MaxAspectRatioBreakpoints.ThreeToFour);
          } else if (result.breakpoints['(max-aspect-ratio: 1/1)']) {
            this.maxAspectRatioSubject.next(MaxAspectRatioBreakpoints.Square);
          } else if (result.breakpoints['(max-aspect-ratio: 4/3)']) {
            this.maxAspectRatioSubject.next(MaxAspectRatioBreakpoints.FourToThree);
          } else if (result.breakpoints['(max-aspect-ratio: 16/9)']) {
            this.maxAspectRatioSubject.next(MaxAspectRatioBreakpoints.Landscape);
          }

          if (result.breakpoints['(min-width: 1920px)']) {
            this.screenWidthSubject.next(ScreenWidthBreakpoints.XLarge);
          }
          else if (result.breakpoints['(min-width: 1280px)']) {
            this.screenWidthSubject.next(ScreenWidthBreakpoints.Large);
          }
          else if (result.breakpoints['(min-width: 960px)']) {
            this.screenWidthSubject.next(ScreenWidthBreakpoints.Medium);
          }
          else if (result.breakpoints['(min-width: 600px)']) {
            this.screenWidthSubject.next(ScreenWidthBreakpoints.Small);
          }
          else {
            this.screenWidthSubject.next(ScreenWidthBreakpoints.XSmall);
          }
        } else {
          this.maxAspectRatioSubject.next(MaxAspectRatioBreakpoints.Landscape);
          this.screenWidthSubject.next(ScreenWidthBreakpoints.Large);
        }
      });
  }
}


// see https://material.angular.io/cdk/layout/overview
//
// PORTRAIT
// HandsetPortrait	  |  < 600px        | (max-width: 599.98px) and (orientation: portrait)
// TabletPortrait	    |  [600px; 840px] | (min-width: 600px) and (max-width: 839.98px) and (orientation: portrait)
// WebPortrait	      | > 840px         | (min-width: 840px) and (orientation: portrait)
// LANDSCAPE
// HandsetLandscape	  | < 960px         | (max-width: 959.98px) and (orientation: landscape)
// TabletLandscape	  | [960px; 1280px] | (min-width: 960px) and (max-width: 1279.98px) and (orientation: landscape)
// WebLandscape	      | > 1280px        | (min-width: 1280px) and (orientation: landscape)



// XSmall	(max-width: 599.98px)
// Small	(min-width: 600px) and (max-width: 959.98px)
// Medium	(min-width: 960px) and (max-width: 1279.98px)
// Large	(min-width: 1280px) and (max-width: 1919.98px)
// XLarge	(min-width: 1920px)